import { FormEvent, useEffect, useRef, useState } from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useAppSelector } from '../../hooks/redux';
import { useSilentGuard } from '../../hooks/useSilentGuard';
import { submitRatingStart } from '../../services/leads';
import { clientError, musbLoginUrl, ServerErrorCode, toAppError } from '../../lib/errorCodes';
import { EMAIL_RE, PHONE_RE } from '../../lib/validation';
import { notify } from '../notify/notify';
import { FieldLabel, TextInput, FormButton, HoneypotInput } from '../forms/FormFields';

const REDIRECT_DELAY_MS = 2500;

export default function HeroStartForm() {
  const content = useAppSelector((s) => s.geo.content);
  const countryCode = useAppSelector((s) => s.geo.countryCode);
  const { state, setHoneypot, validate, reset } = useSilentGuard();
  const [form, setForm] = useState({ name: '', email: '', phone: '' });
  const [loading, setLoading] = useState(false);
  const [redirecting, setRedirecting] = useState(false);
  const [done, setDone] = useState<{ email: string; loginUrl: string } | null>(null);
  const redirectTimer = useRef<number>();

  useEffect(() => () => window.clearTimeout(redirectTimer.current), []);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (loading || redirecting) return;

    const name = form.name.trim();
    const email = form.email.trim().toLowerCase();
    const phone = form.phone.trim();

    if (!name || !email || !phone) {
      notify.appError(clientError('REQUIRED_FIELDS', 'Please enter your name, work email, and phone.'));
      return;
    }
    if (!EMAIL_RE.test(email)) {
      notify.appError(clientError('INVALID_EMAIL'));
      return;
    }
    if (!PHONE_RE.test(phone)) {
      notify.appError(clientError('INVALID_PHONE'));
      return;
    }

    setLoading(true);
    try {
      const gate = await validate();
      if (!gate.ok) {
        notify.appError(gate.error);
        return;
      }

      const res = await submitRatingStart({ name, email, phone, country: countryCode, silent: gate.payload });
      if (res.emailSent === false) {
        const { title, message, code } = clientError('EMAIL_NOT_DELIVERED');
        notify.warning({ title, message, code, requestId: res.leadId?.slice(0, 8).toUpperCase() });
        return;
      }
      setDone({ email, loginUrl: res.loginUrl || musbLoginUrl(email) });
      notify.success({
        title: 'Check your inbox',
        message: `We’ve emailed your Micus workspace URL and temporary app password to ${email}.`,
      });
      reset();
    } catch (err) {
      const info = toAppError(err);
      if (info.code === ServerErrorCode.LEAD_EMAIL_EXISTS) {
        const target = info.redirectUrl || musbLoginUrl(email);
        const go = () => window.location.assign(target);
        setRedirecting(true);
        notify.info({
          title: info.title,
          message: 'Redirecting you to your Micus workspace to sign in…',
          code: info.code,
          requestId: info.requestId,
          action: { label: 'Go now', onClick: go },
          duration: REDIRECT_DELAY_MS + 1000,
        });
        redirectTimer.current = window.setTimeout(go, REDIRECT_DELAY_MS);
        return;
      }
      notify.appError(info);
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28, boxShadow: '0 8px 40px rgba(194,24,91,0.10)', textAlign: 'center' }}>
        <CheckCircle2 size={40} color="#C2185B" style={{ margin: '0 auto 12px' }} />
        <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1A0A10', margin: '0 0 8px' }}>Check your inbox</h3>
        <p style={{ fontSize: 14, color: '#6B3A52', margin: '0 0 18px', lineHeight: 1.6 }}>
          Your Micus workspace is ready. We’ve sent the sign-in URL, your temporary app password and the next steps to{' '}
          <strong style={{ color: '#1A0A10' }}>{done.email}</strong>.
        </p>
        <a
          href={done.loginUrl}
          style={{
            display: 'inline-block',
            padding: '11px 22px',
            borderRadius: 12,
            background: 'linear-gradient(135deg, #C2185B, #E91E63)',
            color: 'white',
            fontWeight: 800,
            fontSize: 14,
            textDecoration: 'none',
          }}
        >
          Go to workspace sign-in
        </a>
        <p style={{ fontSize: 12, color: '#9B6E7E', margin: '14px 0 0', lineHeight: 1.5 }}>
          Didn’t get it in a few minutes? Check spam, or{' '}
          <button
            type="button"
            onClick={() => setDone(null)}
            style={{ border: 'none', background: 'none', padding: 0, color: '#C2185B', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}
          >
            send a new app password
          </button>
          .
        </p>
      </div>
    );
  }

  return (
    <form
      id="start"
      onSubmit={onSubmit}
      style={{ background: 'white', borderRadius: 20, border: '1px solid #F0C8D8', padding: 28, boxShadow: '0 8px 40px rgba(194,24,91,0.10)' }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <p style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: '#C2185B', margin: 0 }}>
          {content.countryLabel} · Start
        </p>
        <span
          style={{
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'white',
            background: 'linear-gradient(135deg, #C2185B, #E91E63)',
            padding: '4px 10px',
            borderRadius: 999,
            boxShadow: '0 4px 14px rgba(233,30,99,0.35)',
          }}
        >
          {content.hero.comboHighlight}
        </span>
      </div>
      <h3 style={{ fontSize: 20, fontWeight: 800, color: '#1A0A10', margin: '0 0 6px' }}>{content.hero.formTitle}</h3>
      <p style={{ fontSize: 13.5, color: '#6B3A52', margin: '0 0 20px', lineHeight: 1.55 }}>{content.hero.formSubtitle}</p>

      <HoneypotInput value={state.honeypot} onChange={setHoneypot} />

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div>
          <FieldLabel htmlFor="lead-name">Full name</FieldLabel>
          <TextInput id="lead-name" name="name" autoComplete="name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} placeholder="Your name" required />
        </div>
        <div>
          <FieldLabel htmlFor="lead-email">Work email</FieldLabel>
          <TextInput id="lead-email" name="email" type="email" autoComplete="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} placeholder="you@company.com" required />
        </div>
        <div>
          <FieldLabel htmlFor="lead-phone">Phone</FieldLabel>
          <TextInput id="lead-phone" name="phone" type="tel" autoComplete="tel" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} placeholder={countryCode === 'US' ? '+1 …' : '+91 …'} required />
        </div>
        <FormButton type="submit" loading={loading} disabled={redirecting}>
          {redirecting ? 'Redirecting to your workspace…' : content.hero.submitLabel}
        </FormButton>
        <p style={{ fontSize: 11.5, color: '#9B6E7E', margin: 0, lineHeight: 1.5, textAlign: 'center' }}>
          By continuing you agree to our <a href="/terms" style={{ color: '#C2185B', fontWeight: 600 }}>Terms</a> and <a href="/privacy" style={{ color: '#C2185B', fontWeight: 600 }}>Privacy Policy</a>.
          No visible captcha — protected silently against automated abuse.
        </p>
      </div>
    </form>
  );
}
